import React from "react";

function formatNotificationTime(value) {
  const timestamp = value ? new Date(value).getTime() : 0;
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString([], { day: "numeric", month: "short" });
}

function NotificationBell({
  hostRef,
  open,
  unreadCount,
  notifications,
  loading,
  error,
  onToggle,
  onItemClick,
  onMarkAllRead
}) {
  const items = Array.isArray(notifications) ? notifications : [];
  const buttonRef = React.useRef(null);
  const [panelStyle, setPanelStyle] = React.useState(null);

  const updatePanelPosition = React.useCallback(() => {
    if (!open || typeof window === "undefined") return;
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const viewportWidth = Math.max(Number(window.innerWidth || 0), 320);
    const viewportHeight = Math.max(Number(window.innerHeight || 0), 320);
    const margin = viewportWidth <= 640 ? 12 : 16;
    const gap = 12;
    const width = Math.min(380, Math.max(280, viewportWidth - margin * 2));
    const top = Math.min(
      Math.max(margin, rect.bottom + gap),
      Math.max(margin, viewportHeight - margin - 240)
    );
    const left = Math.min(
      Math.max(margin, rect.right - width),
      Math.max(margin, viewportWidth - margin - width)
    );
    const availableHeight = Math.max(160, viewportHeight - top - margin);

    setPanelStyle({
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${availableHeight}px`,
      "--notification-list-max-height": `${Math.max(120, availableHeight - 112)}px`
    });
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }

    updatePanelPosition();
    const handleViewportChange = () => updatePanelPosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, updatePanelPosition]);

  return (
    <div ref={hostRef} className={`notification-shell ${open ? "open" : ""}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        className={`notification-bell-button ${unreadCount > 0 ? "has-unread" : ""}`.trim()}
        onClick={onToggle}
        aria-expanded={Boolean(open)}
        aria-haspopup="dialog"
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
      >
        <svg viewBox="0 0 24 24" className="notification-bell-icon" aria-hidden="true">
          <path
            d="M12 4a4 4 0 00-4 4v2.2c0 1.2-.4 2.4-1.1 3.4L5.6 15a1 1 0 00.8 1.6h11.2a1 1 0 00.8-1.6l-1.3-1.4c-.7-1-.1-2.2-1.1-3.4V8a4 4 0 00-4-4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path d="M10 19a2 2 0 004 0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
        {unreadCount > 0 && <span className="notification-bell-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {open && (
        <div className="notification-panel" role="dialog" aria-label="Notifications" style={panelStyle || undefined}>
          <div className="notification-panel-header">
            <div>
              <div className="analysis-label">Notifications</div>
              <div className="notification-panel-title">Updates for you</div>
            </div>
            <button
              type="button"
              className="tool-chip"
              onClick={onMarkAllRead}
              disabled={!unreadCount}
            >
              Mark all read
            </button>
          </div>

          {loading && <div className="question-meta">Loading notifications...</div>}
          {error && <div className="error-text">{error}</div>}

          {!loading && !items.length && !error && (
            <div className="notification-empty">
              <div className="notification-empty-title">All clear</div>
              <div className="question-meta">New invites and app updates will appear here.</div>
            </div>
          )}

          {items.length > 0 && (
            <div className="notification-list">
              {items.map((notification) => (
                <button
                  key={notification.id || notification.createdAt}
                  type="button"
                  className={`notification-item ${notification.readAt ? "is-read" : "is-unread"}`.trim()}
                  onClick={() => onItemClick(notification)}
                >
                  <div className="notification-item-head">
                    <span className="notification-item-title">{notification.title || "Notification"}</span>
                    <span className="notification-item-time">{formatNotificationTime(notification.createdAt)}</span>
                  </div>
                  {notification.message && <div className="notification-item-message">{notification.message}</div>}
                  <div className="notification-item-meta">
                    <span>{notification.actor?.username ? `@${notification.actor.username}` : notification.actor?.name || "InkSolver"}</span>
                    {!notification.readAt && <span className="notification-item-pill">Unread</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(NotificationBell);
